import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Building2, Target, Eye, History, Users, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Icon mapping
const iconMap = {
  'Award': Award,
  'Users': Users,
  'Building2': Building2,
  'History': History,
  'Target': Target,
  'Eye': Eye
};

export default function AboutPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/about`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching About data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="py-12">
          <div className="container-cish text-center">Loading...</div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="py-12">
          <div className="container-cish text-center">Content not available</div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main id="main-content" className="py-8 md:py-12">
        <div className="container-cish">
          {/* Page Header */}
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#0E6D2B] mb-3">
              {data.page_title}
            </h1>
            <p className="text-gray-600 max-w-3xl mx-auto text-sm md:text-base">
              {data.page_subtitle}
            </p>
          </div>

          {/* About Content */}
          <div className="grid lg:grid-cols-3 gap-6 md:gap-8 mb-12">
            <div className="lg:col-span-2">
              <div className="prose prose-green max-w-none">
                <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                  {data.main_paragraph_1}
                </p>
                <p className="text-gray-700 leading-relaxed mt-4 text-sm md:text-base">
                  {data.main_paragraph_2}
                </p>
              </div>
            </div>
            <div className="bg-[#F4F8EF] rounded-xl p-6">
              {data.campus_image_url && (
                <>
                  <img
                    src={typeof data.campus_image_url === 'string' && data.campus_image_url.startsWith('/api') ? `${API_URL}${data.campus_image_url}` : data.campus_image_url}
                    alt="CISH Campus"
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                  <p className="text-sm text-gray-600 text-center">{data.campus_image_caption}</p>
                </>
              )}
            </div>
          </div>

          {/* Vision & Mission */}
          <div id="vision" className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-gradient-to-br from-[#0E6D2B] to-[#21A366] rounded-xl p-6 md:p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-8 h-8" />
                <h2 className="text-xl md:text-2xl font-bold">Our Vision</h2>
              </div>
              <p className="text-white/90 leading-relaxed text-sm md:text-base">
                {data.vision_text}
              </p>
            </div>
            <div className="bg-gradient-to-br from-[#F59E0B] to-[#D97706] rounded-xl p-6 md:p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <Target className="w-8 h-8" />
                <h2 className="text-xl md:text-2xl font-bold">Our Mission</h2>
              </div>
              <p className="text-white/90 leading-relaxed text-sm md:text-base">
                {data.mission_text}
              </p>
            </div>
          </div>

          {/* History */}
          <div id="history" className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <History className="w-6 h-6 text-[#0E6D2B]" />
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">Our History</h2>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="space-y-6">
                {data.history_milestones && data.history_milestones.map((milestone, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-20 shrink-0 text-[#0E6D2B] font-bold">{milestone.year}</div>
                    <p className="text-gray-700 text-sm md:text-base">{milestone.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Key Achievements */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {data.achievements && data.achievements.map((stat, index) => {
              const IconComponent = iconMap[stat.icon] || Award;
              return (
                <div key={index} className="bg-[#F4F8EF] rounded-xl p-5 text-center">
                  <IconComponent className="w-8 h-8 mx-auto text-[#0E6D2B] mb-3" />
                  <div className="text-2xl md:text-3xl font-bold text-[#0E6D2B]">{stat.value}</div>
                  <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
